import React, {Component} from 'react';
import {Button, Card, Checkbox, Col, Dropdown, Icon, Input, List, Menu, Modal, Row, Tooltip, Tree, Typography} from "antd";
import * as R from 'ramda';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {DropTarget} from "react-dnd";
import {NativeTypes} from "react-dnd-html5-backend";
import * as PropTypes from "prop-types";
import {DeleteButton} from "../Projects/DeleteButton";
import moment from "moment";
import {convertToDate} from "../../projections";

const { TreeNode } = Tree;

function downloadFile(url) {
  let link = document.createElement('a');
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

class DeleteFileDropdown extends Component {
  state={};
  render() {
    return <Dropdown overlay={this.props.overlay} trigger={["click"]} onVisibleChange={visible => this.setState({visible})}>
      <FontAwesomeIcon icon={'angle-' + (!this.state.visible ? 'right' : 'down')} />
    </Dropdown>;
  }
}

DeleteFileDropdown.propTypes = { overlay: PropTypes.any };

class FileName extends Component {
  state = {
    editing: false,
  };

  static propTypes = {
    onDownload: PropTypes.func,
    onRename: PropTypes.func,
    file: PropTypes.any
  };

  render() {
    return <Typography.Text style={{margin: 0, width: 'inherit'}} editable={{
      editing: this.state.editing,
      onStart: () => this.setState({ editing: true }),
      onChange: value => {
        if (value !== this.props.file.name) this.props.onRename(this.props.file, value);
        this.setState({ editing: false });
      }
    }}>{this.state.editing ? this.props.file.name :
      <a onClick={() => this.props.onDownload(this.props.file)}>{this.props.file.name}</a>
    }</Typography.Text>;
  }
}

class DirectoryView extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      selectedFolders: ['/'],
      newFolderVisible: false,
      newFolderError: false,
      newFolderName: '',
      disableDelete: true,
    };
    this.uploadRef = React.createRef();
  }

  selectedFiles = () => {
    /**
     * @returns All files and folders contained within the currently selected folder.
     */
    const { selectedFolders } = this.state;
    const { fileTree: { list }} = this.props;
    return R.innerJoin((item, path) => item.path === path, list, selectedFolders);
  };

  isDirectoryProtected = (selectedKeys) => {
    for (let key of selectedKeys) {
      if (R.includes(key, ['/', '/Plans', '/Specs'])) return true;
    }
    return false;
  };

  handleSelect = (selectedKeys = ['/'], event) => {
    const { fileTree: { list }} = this.props;
    if (!Array.isArray(selectedKeys) || !selectedKeys.length) selectedKeys = ['/'];
    let current = R.innerJoin(
      (item, path) => item.path === path,
      list,
      selectedKeys
    );
    this.setState({
      selectedFolders: selectedKeys,
      disableDelete: this.isDirectoryProtected(selectedKeys)
    });
  };

  handleAddFolder = () => {
    this.setState({ newFolderVisible: true });
  };

  handleDeleteFolder = () => {
    const { selectedFolders } = this.state;
    const { onDeleteFolder, fileTree: { list }} = this.props;
    if (selectedFolders.length > 0) {
      let path = selectedFolders[0];
      let dir_node = list.find(x => x.mimetype === 'application/x-directory' && path === ((x.path === '/' ? '' : x.path) + '/' + x.name));
      if (dir_node && onDeleteFolder) onDeleteFolder(dir_node.id);
    }
  };

  handleAddFolderOk = () => {
    const { selectedFolders, newFolderName } = this.state;
    if (!newFolderName || newFolderName === '') {
      this.setState({ newFolderError: true });
      return;
    }
    const { onNewFolder } = this.props;
    let path = '/';
    if (selectedFolders.length > 0) {
      path = selectedFolders[0];
    }
    this.setState({ newFolderVisible: false, newFolderName: '', newFolderError: false });
    if (onNewFolder) onNewFolder(path, newFolderName);
  };

  handleDeleteConfirm = (deleteAction) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this?',
      onOk() { deleteAction(); }
    });
  };

  handleAddFolderCancel = () => {
    this.setState({ newFolderVisible: false, newFolderName: '', newFolderError: false });
  };

  handleAddFolderNameChange = (e) => {
    this.setState({ newFolderName: e.target.value });
  };

  handleDownloadAllFiles = () => {
    const { onArchiveClear, onDownloadArchive, archive } = this.props;
    if (archive && archive.state === 'ready') {
      downloadFile(archive.detail.fields.url);
      if (onArchiveClear) onArchiveClear(archive.detail.associatedid, archive.detail.historyid);
    } else {
      if (onDownloadArchive) onDownloadArchive();
    }
  };

  handleUploadClick = () => {
    this.uploadRef.current.click();
  };

  handleDelete = (id) => {
    const { onDelete } = this.props;
    if (onDelete) onDelete(id);
  };

  doConfirmUpload = ({currentFiles, queuedFiles, path, onUpload, onDelete}) => {
    let confirmCount = queuedFiles.length;
    new Promise((resolve, reject) => queuedFiles.forEach((file, idx, arr) => {
      const fileOverlaps = currentFiles.map(i => i.name).includes(file.name);
      const overlappingFile = currentFiles.filter(i => i.name === file.name)[0];
      if (fileOverlaps) Modal.confirm({
        title: `Do you want to overwrite ${file.name}?`,
        style: { zIndex: 10000 },
        onOk() {
          queuedFiles[idx].onSuccess = fileOverlaps && onDelete && (() => onDelete(overlappingFile.id));
          if (--confirmCount === 0) resolve();
        },
        onCancel() {
          delete queuedFiles[idx];
          if (--confirmCount === 0) resolve();
        }
      });
      else if (--confirmCount === 0) resolve();
    })).then(() => {
      queuedFiles = queuedFiles.filter(i => i);
      if (queuedFiles.length && onUpload) onUpload(path, queuedFiles);
    });
  };

  getCurrentPath = () => {
    /**
     * @returns The currently selected folder or, if no folder is selected, the root folder.
     */
    const { selectedFolders } = this.state;
    let path = '/';
    if (selectedFolders.length > 0) path = selectedFolders[0];
    return path;
  };

  getCurrentDirectoryFiles = () => {
    /**
     * @returns All files, excluding folders, inside the currently selected folder.
     */
    const { selectedFolders } = this.state;
    let { fileTree: { list } } = this.props;
    list = list || [];
    list = list.filter(item => item.mimetype !== 'application/x-directory');
    return R.innerJoin((item, path) => item.path === path, list, selectedFolders);
  };

  handleUploadFiles = (files) => {
    const { onUpload, onDelete } = this.props;
    let queuedFiles = Array.from(files);
    let path = this.getCurrentPath();
    let currentFiles = this.getCurrentDirectoryFiles();
    return this.doConfirmUpload({currentFiles, queuedFiles, path, onUpload, onDelete});
  };

  renderFolders = (items) => {
    if (!items) return <React.Fragment/>;
    let entries = Object.entries(items);
    let keySort = R.sort(R.ascend(R.prop(0)));
    return keySort(entries).map(([key, item]) => {
      if (item._children) {
        return (
          <TreeNode
            icon={<Icon type="folder-open" theme="filled" />}
            title={<span>{item.label || key}</span>}
            key={item.path || key}
          >
            {this.renderFolders(item._children)}
          </TreeNode>
        );
      } else {
        return (
          <TreeNode
            icon={<Icon type="folder-open" theme="filled" />}
            title={<span>{item.label || key}</span>}
            key={item.path || key}
            isLeaf
          />
        );
      }
    });
  };

  renderTree = (items) => {
    const { selectedFolders } = this.state;
    return (
      <Tree
        defaultExpandAll
        showIcon
        switcherIcon={<Icon type='caret-down'/>}
        selectedKeys={selectedFolders}
        onSelect={this.handleSelect}
      >
        {this.renderFolders(items)}
      </Tree>
    );
  };

  mimeTypeMap = (mimeType) => {
    if (!mimeType) {
      return 'file';
    }
    if (mimeType.match(/^audio\//)) {
      return 'file-audio';
    }
    if (mimeType.match(/^text\//)) {
      return 'file-alt';
    }
    if (mimeType.match(/^image\//)) {
      return 'file-image';
    }
    if (mimeType.match(/^video\//)) {
      return 'file-video';
    }
    switch (mimeType) {
      case 'application/zip':
      case 'application/x-bzip':
      case 'application/x-bzip2':
      case 'application/x-7z-compressed':
        return 'file-archive';

      case 'application/msword':
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      case 'application/rtf':
      case 'application/application/vnd.oasis.opendocument.text':
        return 'file-word';

      case 'application/pdf':
        return 'file-pdf';

      case 'application/vnd.ms-excel':
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      case 'application/vnd.oasis.opendocument.spreadsheet':
        return 'file-excel';

      case 'application/vnd.ms-powerpoint':
      case 'application/vnd.openxmlformats-officedocument.presentational.presentation':
      case 'application/vnd.oasis.opendocument.presentation':
        return 'file-powerpoint';

      default:
        return 'file';
    }
  };

  renameFile = (item, name) => {
    const { onRename } = this.props;
    if (onRename) onRename(item, name);
  };

  downloadFile = (item) => {
    const { onDownload } = this.props;
    if (onDownload) onDownload(item);
  };

  renderFiles = () => {
    const { selectedFolders } = this.state;
    let { fileTree: { list }, onSetAddendum, permissions: {update: canUpdate} } = this.props;
    list = list || [];
    list = list.filter(item => item.mimetype !== 'application/x-directory');
    let current = R.innerJoin(
      (item, path) => item.path === path,
      list,
      selectedFolders
    );
    return (
      <div>
        {current.map(item => {
          const menu = <Menu>
            <Menu.Item key='1' onClick={() => this.handleDeleteConfirm(() => this.handleDelete(item.id))}>Delete</Menu.Item>
          </Menu>;

          const timestamp = convertToDate(item.datecreated);

          return <div key={item.id}>
            <div style={{display: 'flex', alignItems: 'center', width: '100%'}}>
              <a style={{marginRight: 12}} onClick={() => this.downloadFile(item)}><FontAwesomeIcon icon={this.mimeTypeMap(item.mimetype)}/></a>
              <FileName onDownload={this.downloadFile} onRename={this.renameFile} file={item}/>
            </div>
            <div>
              {onSetAddendum ?
                <Checkbox disabled={!canUpdate} checked={item.isaddendum} style={{whiteSpace: 'nowrap'}}
                          onChange={(e) => onSetAddendum(item.id, e.target.checked)}>Addendum</Checkbox> : null}
              <Tooltip placement="left" title={timestamp.format('LT')}>
                <span style={{ paddingLeft: 8, paddingRight: 8 }}>{timestamp.format('l')}</span>
              </Tooltip>
              {canUpdate && <div style={{ marginLeft: 8, marginRight: 8 }}>
                {/*<span style={{marginRight: 6}}>*/}
                {/*<DocumentDeleteButton document={{resource: item}} onDelete={() => null} type='file' title='Rename File' icon='edit'/>*/}
                {/*</span>*/}
                <DeleteButton document={{ resource: item }} onDelete={this.handleDelete} type='file'
                              title='Delete File' icon='delete'/>
              </div>}
            </div>
          </div>;
        })}
      </div>
    );
  };

  renderText = (items) => {
    return (
      <pre>
        {JSON.stringify(items, null, 2)}
      </pre>
    );
  };

  render() {
    const { fileTree: { tree }, archive, style, controlsExtra, uploading = {} } = this.props;
    const { newFolderVisible, newFolderName, newFolderError, disableDelete } = this.state;
    let downloadButtonText = 'Download All Files';
    let downloadButtonAnimate = false;
    if (archive) {
      switch (archive.state) {
        case 'pending':
          downloadButtonText = 'Asking server for a zip...';
          downloadButtonAnimate = true;
          break;
        case 'requested':
          downloadButtonText = 'Zipping your files...';
          downloadButtonAnimate = true;
          break;
        case 'accepted':
          downloadButtonText = 'Zipping your files...';
          downloadButtonAnimate = true;
          break;
        case 'ready':
          downloadButtonText = 'Archive ready - click again to download';
          break;
        case 'rejected':
          downloadButtonText = 'Archive could not be created: ' + JSON.parse(archive.detail.fields).reject;
          break;
        default: break;
      }
    }
    let downloadButtonIconStyle = {
      visibility: downloadButtonAnimate ? 'hidden' : 'visible'
    };
    return (
      <FileDropZone
        onFileDrop={this.handleUploadFiles}
        className='dashboard-files'
        fileTree={tree}
      >
        <Card
          style={style}
        >
          <Modal
            title='Uploading Files'
            visible={R.path(['loading'], uploading)}
            footer={null}
          >
            <List
              bordered
              dataSource={Object.keys(R.filter(i => i, R.omit(['loading'], uploading)))}
              renderItem={item => <List.Item><List.Item.Meta title={item}/><Icon type='loading'/></List.Item>}
            />
          </Modal>
          <Modal
            title='New Folder'
            visible={newFolderVisible}
            onOk={this.handleAddFolderOk}
            onCancel={this.handleAddFolderCancel}
          >
            Folder Name
            <Input value={newFolderName} onChange={this.handleAddFolderNameChange}/>
            { newFolderError && <Typography>
              <Typography.Paragraph>
                <Typography.Text type='secondary'>
                  This field cannot be left blank.
                </Typography.Text>
              </Typography.Paragraph>
            </Typography>}
          </Modal>
          <Row>
            <Col xs={8} xl={7} xxl={6} className='directory-view'>
              <Row type={'flex'}>
                <Col span={24} className='controls'>
                  <Button disabled={!this.props.permissions.update} onClick={this.handleAddFolder} style={{marginBottom: 8}}>
                    <Icon type="folder-add" theme="filled"/>
                    <span className={'hidden inline' + (this.props.displayControls && ' visible-lg')}>New Folder</span>
                  </Button><br />
                  <Button disabled={!this.props.permissions.update || disableDelete}
                          onClick={() => this.handleDeleteConfirm(this.handleDeleteFolder)}>
                    <Icon type="delete" theme="filled"/>
                    <span className={'hidden inline' + (this.props.displayControls && ' visible-lg')}>Delete Folder</span>
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col span={24} className='content'>
                  {this.renderTree(tree)}
                </Col>
              </Row>
            </Col>
            <Col xs={16} xl={17} xxl={18} className='files-view'>
              <Row type={'flex'}>
                {controlsExtra &&
                <Col className='controls-extra'>
                  {controlsExtra}
                </Col>}
                <Col className='controls'>
                  <Button onClick={this.handleDownloadAllFiles} type='primary' loading={downloadButtonAnimate}>
                    <Icon type='download' style={downloadButtonIconStyle}/>
                    <span className='hidden inline visible-sm'>{downloadButtonText}</span>
                  </Button>
                  <Button
                    disabled={!this.props.permissions.update}
                    onClick={this.handleUploadClick}
                    className='upload-button'
                  >
                    <Icon type='upload'/><span className='hidden inline visible-sm'>Upload</span>
                    <input
                      type='file' multiple onChange={e => this.handleUploadFiles(e.target.files)} ref={this.uploadRef}/>
                  </Button>
                </Col>
              </Row>
              <Row className='content-wrapper'>
                <Col span={24} className='content'>
                  {this.renderFiles()}
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>
      </FileDropZone>
    );
  }
}

DirectoryView.propTypes = {
  permissions: PropTypes.object,
  fileTree: PropTypes.any,
  archive: PropTypes.any,
  uploading: PropTypes.any,
  displayControls: PropTypes.any,
  loader: PropTypes.any,
  onDownload: PropTypes.any,
  onUpload: PropTypes.any,
  onRename: PropTypes.any,
  onDelete: PropTypes.any,
  onDeleteFolder: PropTypes.any,
  onNewFolder: PropTypes.any,
  onDownloadArchive: PropTypes.any,
  onArchiveClear: PropTypes.any,
  onSetAddendum: PropTypes.any,
  controlsExtra: PropTypes.any,
};

const _FileDropZone = (props) => {
  const { connectDropTarget, isOver, ...rest } = props;
  return connectDropTarget(<div {...rest}/>);
};

const FileDropZone = DropTarget(
  NativeTypes.FILE,
  {
    drop(props, monitor) {
      const droppedFiles = monitor.getItem().files;
      if (props.onFileDrop) {
        props.onFileDrop(droppedFiles);
      }
    }
  },
  (connect, monitor) => {
    return {
      connectDropTarget: connect.dropTarget(),
      isOver: monitor.isOver(),
    }
  }
)(_FileDropZone);

export default DirectoryView;



// WEBPACK FOOTER //
// ./src/components/Boilerplate/DirectoryView.js